// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: one place for the phone rail's tab labels and icons, shared by the
// rail itself, the Settings > Mobile rail editor and the settings list's
// miniature. Brand tabs (WhatsApp, ClickUp) draw their logos; the rest use
// the app's icon set.

import {ClickUpLogo} from '@app/features/ui/components/icons/ClickUpLogo';
import {WhatsAppLogo} from '@app/features/ui/components/icons/WhatsAppLogo';
import {
	BellRingingIcon,
	CalendarDotsIcon,
	ChatCircleIcon,
	ClockIcon,
	EnvelopeSimpleIcon,
	GridFourIcon,
	HouseIcon,
	type Icon,
	type IconWeight,
} from '@app/features/ui/icons/Icons';
import {ScratchpadIcon} from '@app/features/ui/icons/ScratchpadIcon';
import type {MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {IconTerminal2} from '@tabler/icons-react';
import type {MobileRailTabId} from './MobileRailTypes';

export const MOBILE_RAIL_TAB_LABELS: Record<MobileRailTabId, MessageDescriptor> = {
	home: msg({message: 'Home', comment: 'Phone rail tab label: the Home page with the sorted conversations.'}),
	chats: msg({message: 'Chats', comment: 'Phone rail tab label: the chats list.'}),
	mail: msg({message: 'Mail', comment: 'Phone rail tab label: the Outlook mailbox.'}),
	more: msg({message: 'More', comment: 'Phone rail tab label: the panel with everything else.'}),
	calendar: msg({message: 'Calendar', comment: 'Phone rail tab label: the calendar.'}),
	scratchpad: msg({message: 'Scratchpad', comment: 'Phone rail tab label: the scratchpad notes.'}),
	reminders: msg({message: 'Reminders', comment: 'Phone rail tab label: message reminders.'}),
	whatsapp: msg({message: 'WhatsApp', comment: 'Phone rail tab label: linked WhatsApp chats.'}),
	clickup: msg({message: 'ClickUp', comment: 'Phone rail tab label: the ClickUp inbox.'}),
	agents: msg({
		message: 'Agents',
		comment: "Phone rail tab label: the coding agents page (the user's T3 Code threads).",
	}),
	scheduled: msg({
		message: 'Scheduled',
		comment:
			'Phone rail tab label: the Scheduled page listing everything coming up (reminders, scheduled messages, automations, digests, promises).',
	}),
};

export const MOBILE_RAIL_ASK_LABEL: MessageDescriptor = msg({
	message: 'Ask',
	comment: 'The dark pill on the phone rail that opens the assistant. Always present.',
});

/** Tabs drawn with the app icon set; the brand tabs are handled separately. */
const MOBILE_RAIL_TAB_ICONS: Partial<Record<MobileRailTabId, Icon>> = {
	home: HouseIcon,
	chats: ChatCircleIcon,
	mail: EnvelopeSimpleIcon,
	more: GridFourIcon,
	calendar: CalendarDotsIcon,
	scratchpad: ScratchpadIcon,
	reminders: BellRingingIcon,
	scheduled: ClockIcon,
};

export interface MobileRailTabIconProps {
	tab: MobileRailTabId;
	size?: number;
	weight?: IconWeight;
	className?: string;
	/** Brand logos in currentColor instead of their brand colour. */
	mono?: boolean;
}

export function MobileRailTabIcon({
	tab,
	size = 18,
	weight = 'regular',
	className,
	mono = false,
}: MobileRailTabIconProps) {
	if (tab === 'whatsapp') {
		return (
			<span
				className={className}
				style={{display: 'inline-flex', lineHeight: 0}}
				data-flx="app.mobile-rail.tab-icon.whatsapp"
			>
				<WhatsAppLogo size={size} mono={mono} />
			</span>
		);
	}
	if (tab === 'clickup') {
		return (
			<span
				className={className}
				style={{display: 'inline-flex', lineHeight: 0}}
				data-flx="app.mobile-rail.tab-icon.clickup"
			>
				<ClickUpLogo size={size} />
			</span>
		);
	}
	if (tab === 'agents') {
		// KONVO: Tabler's terminal-2, at the stroke the app icon set uses for this weight.
		return (
			<IconTerminal2
				size={size}
				stroke={weight === 'bold' || weight === 'fill' ? 2.25 : 1.75}
				className={className}
				aria-hidden={true}
				data-flx="app.mobile-rail.tab-icon.agents"
			/>
		);
	}
	const IconComponent = MOBILE_RAIL_TAB_ICONS[tab] ?? ChatCircleIcon;
	return <IconComponent size={size} weight={weight} className={className} data-flx="app.mobile-rail.tab-icon" />;
}
